import { api } from '@/shared/api';
import type { ApiResponse, Pagination } from '@/shared/types/api';

export interface Resource {
  id: string;
  title: string;
  description?: string;
  category: string;
  type?: 'document' | 'video' | 'link' | 'image';
  fileUrl?: string;
  fileSize?: number;
  uploadedBy: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
  };
  createdAt: string;
  // downloadCount: number;
  tags?: string[];
}

export interface CreateResourceRequest {
  title: string;
  description: string;
  category: string;
  fileUrl: string;
  fileType?: string;
}

export interface UpdateResourceRequest extends Partial<CreateResourceRequest> {
  id: string;
}

export interface GetResourcesResponse {
  resources: Resource[];
  pagination: Pagination;
}

export interface ResourceOverviewResponse {
  totalResources: number;
  last30DaysCount: number;
  categoryCounts: Record<string, number>;
  latestResources: Resource[];
}

export const resourceApi = {
  // Get all resources
  getResources: async (
    page: number = 1,
    limit: number = 10,
    search?: string,
    category?: string,
  ): Promise<GetResourcesResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('search', search);
    if (category) params.append('category', category);

    const response = await api.get<ApiResponse<GetResourcesResponse>>(
      `/resources?${params.toString()}`,
    );
    return response.data;
  },

  // Get resource by ID
  getResource: async (id: string): Promise<Resource> => {
    const response = await api.get<ApiResponse<Resource>>(`/resources/${id}`);
    return response.data;
  },

  // Get resources overview
  getResourcesOverview: async (): Promise<ResourceOverviewResponse> => {
    const response = await api.get<ApiResponse<ResourceOverviewResponse>>(
      '/resources/overview',
    );
    return response.data;
  },

  // Create new resource
  createResource: async (data: CreateResourceRequest): Promise<Resource> => {
    const response = await api.post<ApiResponse<Resource>>('/resources', data);
    return response.data;
  },

  // Delete resource
  deleteResource: async (id: string): Promise<void> => {
    await api.delete<ApiResponse<void>>(`/resources/${id}`);
  },

  // Update resource
  updateResource: async (
    id: string,
    data: Partial<CreateResourceRequest>,
  ): Promise<Resource> => {
    const response = await api.put<ApiResponse<Resource>>(
      `/resources/${id}`,
      data,
    );
    return response.data;
  },

  // Remove file from resource
  removeFile: async (id: string): Promise<Resource> => {
    const response = await api.delete<ApiResponse<Resource>>(
      `/resources/${id}/file`,
    );
    return response.data;
  },

  getUploadUrl: async (
    filename: string,
    module: string,
    contentType: string,
  ): Promise<{ signedUrl: string; token: string; path: string }> => {
    const response = await api.post<
      ApiResponse<{
        signedUrl: string;
        token: string;
        path: string;
      }>
    >('/resources/upload-url', { filename, module, contentType });
    return response.data;
  },

  deleteTempFile: async (path: string): Promise<void> => {
    await api.post<ApiResponse<void>>('/resources/delete-file', { path });
  },
};
